import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import _ from 'lodash';
import { TextInput } from 'Shared/form/TextField';
import * as R from 'ramda';

export const useGetWorkQueueColumns = ({ headerMapping, onFilterChange, searchState, isAssignmentManager }) => {
    const {
        values: {
            result: { workQueueTableHeaders },
            selectedRows = {},
        },
        setFieldValue,
    } = useFormikContext();

    return useMemo(() => {
        if (!workQueueTableHeaders?.headers) {
            return [];
        }
        const headers = R.compose(
            R.sortBy(R.prop('fieldOrder')),
            /**
             * Double-reverse allows for the right-most duplicate to be retained and the left-most be dropped
             */
            R.reverse,
            R.uniqBy(R.prop('fieldSelected')),
            R.reverse
        )(workQueueTableHeaders?.headers || []);

        const customColumns = headers?.map((header) => {
            const mapping = headerMapping[header.fieldSelected];

            const accessor = mapping.accessor || _.camelCase(header.fieldSelected);
            const ariabLabel = `Filter by ${header.nameOfHeader}`;

            return {
                Header: header.nameOfHeader,
                accessor,
                sortName: mapping.sortName || header.fieldSelected,
                id: mapping.sortName || header.fieldSelected,
                canSort: mapping.canSort || false,
                Filter: (
                    <TextInput
                        name={mapping.filterName || header.fieldSelected}
                        placeholder={ariabLabel}
                        onChange={onFilterChange}
                        title={ariabLabel}
                        aria-label={ariabLabel}
                        value={searchState[mapping.filterName || header.fieldSelected] || ''}
                    />
                ),
                canFilter: true,
                ...(mapping.Cell
                    ? { Cell: mapping.Cell }
                    : {}),
            };
        });
        if (isAssignmentManager) {
            const selectionColumn = {
                id: 'selection',
                Header: ({ getToggleAllPageRowsSelectedProps, rows }) => {
                    const { ...defaultProps } = getToggleAllPageRowsSelectedProps();
                    const allPageRowsSelected =
                        rows.length > 0 && rows.every((row) => !!selectedRows[row.original.activityId]);
                    return (
                        <div>
                            <input
                                type="checkbox"
                                {...defaultProps}
                                checked={allPageRowsSelected}
                                onChange={(e) => {
                                    defaultProps.onChange(e);
                                    if (e.target.checked) {
                                        setFieldValue(
                                            'selectedRows',
                                            rows.reduce(
                                                (selectedValues, row) => ({
                                                    ...selectedValues,
                                                    [row.original.activityId]: true,
                                                }),
                                                { ...selectedRows }
                                            )
                                        );
                                    } else {
                                        const newState = { ...selectedRows };
                                        rows.forEach((row) => delete newState[row.original.activityId]);
                                        setFieldValue('selectedRows', newState);
                                    }
                                }}
                            />
                        </div>
                    );
                },
                Cell: ({ row }) => {
                    const { ...rowDefaultProps } = row.getToggleRowSelectedProps();
                    return (
                        <div key={row.original.activityId}>
                            <input
                                type="checkbox"
                                {...rowDefaultProps}
                                checked={selectedRows[row.original.activityId]}
                                onChange={(e) => {
                                    rowDefaultProps.onChange(e);
                                    const newState = { ...selectedRows };
                                    if (e.target.checked) {
                                        newState[row.original.activityId] = true;
                                    } else {
                                        delete newState[row.original.activityId];
                                    }
                                    setFieldValue('selectedRows', newState);
                                }}
                            />
                        </div>
                    );
                },
            };

            selectionColumn.Header.propTypes = {
                getToggleAllPageRowsSelectedProps: PropTypes.func.isRequired,
                rows: PropTypes.arrayOf(
                    PropTypes.shape({
                        original: PropTypes.shape({
                            activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                        }).isRequired,
                    }).isRequired
                ).isRequired,
            };
            selectionColumn.Cell.propTypes = {
                row: PropTypes.shape({
                    getToggleRowSelectedProps: PropTypes.func.isRequired,
                    original: PropTypes.shape({
                        activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    }).isRequired,
                }).isRequired,
            };

            customColumns.unshift(selectionColumn);
        }
        return customColumns;
    }, [
        workQueueTableHeaders?.headers,
        isAssignmentManager,
        headerMapping,
        onFilterChange,
        searchState,
        setFieldValue,
        selectedRows,
    ]);
};
